import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { HttpStatusCode } from "axios";

import { safeReplaceAll } from "../../libs/replace";

import {
  HistoryItem,
  HistorySummary,
  getHistory,
  getHistoryReservationConcurrent,
} from "../../services/historyServices";

import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../hooks/useInfiniteScroll";
import useFullScreenLoading from "../../hooks/useFullScreenLoading";
import useSimplePopup from "../../hooks/useSimplePopup";

import StickyHeader from "../../components/Layout/StickyHeader";
import VerticalRule from "../../components/Rule/Vertical";
import { PostListNoPostIcon } from "../../components/Talk/PostList";
import HorizontalRule from "../../components/Rule/Horizontal";
import CommonSelect, { SelectOption } from "../../components/Select";
import HistoryIncommodityItem from "../../components/History/IncommodityItem";
import HistoryReservationItem from "../../components/History/ReservationItem";
import HistoryRentItem from "../../components/History/RentItem";
import HistoryServiceItem from "../../components/History/ServiceItem";
import CommonCheckbox from "../../components/Input/Checkbox";

type Props = {};

export default function History(props: Props) {
  const PAGE_SIZE = 10;
  const SCROLL_LOAD_START_PAGE = 2;
  const navigate = useNavigate();
  const [historyList, setHistoryList] = useState<HistoryItem[]>([]);
  const [isSticky, setIsSticky] = useState(false);
  const location = useLocation();

  const TYPE_VALUE = {
    ALL: "all",
    INCOMMODITY: "incommodity",
    RESERVATION: "reservation",
    RENT: "rent",
    SERVICE: "service",
  };

  const typeList: SelectOption[] = [
    { value: TYPE_VALUE.ALL, label: "전체" },
    { value: TYPE_VALUE.INCOMMODITY, label: "불편신고" },
    { value: TYPE_VALUE.RESERVATION, label: "공간 예약" },
    { value: TYPE_VALUE.RENT, label: "대여" },
    { value: TYPE_VALUE.SERVICE, label: "신청 서비스" },
  ];

  const getInitType = () => {
    if (location.state?.isReservationLimit) {
      return (
        typeList.find((type) => type.value === TYPE_VALUE.RESERVATION) ??
        typeList[0]
      );
    }
    return typeList[0];
  };

  const [selectedType, setSelectedType] = useState<SelectOption>(getInitType());
  const [summary, setSummary] = useState<HistorySummary>({
    spaceReservation: 0,
    request: 0,
    rent: 0,
    incommodity: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isReservationLimit, setIsReservationLimit] = useState<boolean>(
    location.state?.isReservationLimit ?? false
  );
  const loadingComponent = useFullScreenLoading({
    isLoading,
    testId: "history",
  });
  const openLocationError = useRef(false);
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const loadHistory = async (args?: InfiniteScrollCallbackArgs) => {
    let itemLength = INFINITE_CODE.ERROR;

    setIsLoading(true);
    try {
      const res =
        isReservationLimit && selectedType.value === TYPE_VALUE.RESERVATION
          ? await getHistoryReservationConcurrent({ ...args })
          : await getHistory({
              type: selectedType.value,
              ...args,
            });

      if (res.status === HttpStatusCode.Ok) {
        itemLength = res.data.data.length;
        if (args?.page === 1) {
          setHistoryList(
            res.data.data.map((history) => ({
              ...history,
              createdAt: safeReplaceAll(
                safeReplaceAll(history.createdAt?.split(".")[0], "T", " "),
                "-",
                "/"
              ) as string,
            }))
          );
        } else {
          setHistoryList((prev) => [
            ...prev,
            ...res.data.data.map((history) => ({
              ...history,
              createdAt: safeReplaceAll(
                safeReplaceAll(history.createdAt?.split(".")[0], "T", " "),
                "-",
                "/"
              ) as string,
            })),
          ]);
        }
        setSummary(res.data.summaryForService);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
    return itemLength;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    isLoading,
    callback: loadHistory,
    startPage: SCROLL_LOAD_START_PAGE,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    resetScroll();
    loadHistory({
      page: SCROLL_LOAD_START_PAGE - 1,
      pageSize: PAGE_SIZE,
    });
  }, [selectedType, isReservationLimit]);

  useEffect(() => {
    if (openLocationError.current === false && location?.state?.error) {
      open(location.state.error);
      openLocationError.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    const handleScroll = () => {
      if ((window?.scrollY ?? 0) > 120) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window?.addEventListener("scroll", handleScroll);

    // Clean up function
    return () => {
      window?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="flex flex-col pb-10">
      {loadingComponent}
      <StickyHeader
        title="히스토리"
        onBackClick={() => navigate("/myinfo")}
        className={clsx(["px-5 transition-all text-brand-gray-140 !z-30"])}
        backIconColor={isSticky ? "black" : "white"}
        backgroundColor={!isSticky ? "#132029" : undefined}
      />
      <div className=" bg-brand-gray-140 p-5 pb-[32px] flex flex-col gap-[31px]">
        <div className="text-3xl text-white">히스토리</div>
        <div className="flex h-[64px]">
          <HistorySummaryItem
            count={summary?.spaceReservation ?? 0}
            label="공간예약"
          />
          <VerticalRule className="!h-full" />
          <HistorySummaryItem
            count={summary?.rent ?? 0}
            label="대&nbsp;&nbsp;&nbsp;&nbsp;여"
          />
          <VerticalRule className="!h-full" />
          <HistorySummaryItem
            count={summary?.request ?? 0}
            label="신&nbsp;&nbsp;&nbsp;&nbsp;청"
          />
          <VerticalRule className="!h-full" />
          <HistorySummaryItem
            count={summary?.incommodity ?? 0}
            label="불편신고"
          />
        </div>
      </div>
      <div className="px-5">
        <div className="py-2.5 flex gap-2.5 items-center justify-between flex-wrap">
          <div className="flex gap-[5px] items-center h-[40px]">
            <CommonSelect
              value={selectedType.value}
              optionList={typeList}
              onChange={(value) => {
                setSelectedType(value);
                setIsReservationLimit(false);
              }}
              optionOrder="left"
              variant="transparent"
            />
          </div>
          {selectedType.value === TYPE_VALUE.RESERVATION && (
            <div className="flex items-center gap-2.5 design_min:whitespace-nowrap">
              <CommonCheckbox
                checked={isReservationLimit}
                onClick={setIsReservationLimit}
                testId={"view-restricted-space"}
              />
              이용제한공간 예약내역 보기
            </div>
          )}
        </div>
        <HorizontalRule className="bg-black px-5" margin="0" />
        <div className="pt-5">
          {historyList && historyList.length > 0 ? (
            historyList.map((item, index, items) => {
              switch (item.type) {
                case TYPE_VALUE.INCOMMODITY:
                  return (
                    <HistoryIncommodityItem
                      key={index}
                      index={index}
                      item={item}
                      items={items}
                    />
                  );
                case TYPE_VALUE.RESERVATION:
                  return (
                    <HistoryReservationItem
                      key={index}
                      index={index}
                      item={item}
                      items={items}
                    />
                  );
                case TYPE_VALUE.RENT:
                  return (
                    <HistoryRentItem
                      key={index}
                      index={index}
                      item={item}
                      items={items}
                    />
                  );
                case TYPE_VALUE.SERVICE:
                  return (
                    <HistoryServiceItem
                      key={index}
                      index={index}
                      item={item}
                      items={items}
                    />
                  );
                default:
                  return null;
              }
            })
          ) : (
            <div className="flex flex-col  items-center justify-center w-full h-40 gap-3 text-brand-neutral-60">
              <PostListNoPostIcon />
              조회된 내역이 없어요
            </div>
          )}
        </div>
      </div>
      {simplePopupComponent}
    </div>
  );
}

export type HistorySummaryItemProps = {
  count?: number | string;
  label?: string;
};

export const HistorySummaryItem = ({
  count,
  label,
}: HistorySummaryItemProps) => {
  return (
    <div className="px-4 py-[5px] flex flex-col gap-2 items-center flex-1 min-w-[64px]">
      <div className="text-2xl font-semibold text-white">{count}</div>
      <div className="text-sm text-brand-neutral-50 font-medium whitespace-nowrap">
        {label}
      </div>
    </div>
  );
};

export const HistoryDownArrow = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
